import type {
  ProposalChangeLine,
  ProposalCollaborationEnvelope,
  ProposalContentOperation,
  ProposalReviewerChange,
  ProposalTimelineEntry,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import {
  proposalChangeActionFor,
  proposalChangeFieldLabel,
  proposalChangeKey,
  proposalChangeValueText,
  proposalReviewerAccessLabel,
  type ProposalChangeDescriptionContext,
  type ProposalChangeDescriptor,
} from "work/proposals/negotiation/proposal-change-description";
import {
  isProposalFeeScheduleTeamMemberProperty,
  proposalFeeScheduleAspectText,
  proposalFeeScheduleColumnFromConflictKey,
  proposalFeeScheduleRestrictedTeamMemberText,
  proposalFeeScheduleTeamMemberProperty,
  readProposalFeeScheduleAspectValue,
  readProposalFeeScheduleCategoryValues,
} from "work/proposals/negotiation/proposal-fee-schedule-aspects";
import { isProposalFeeScheduleCategoryPath } from "work/proposals/negotiation/proposal-fee-schedule-change-summary";
import { proposalCollectionItemHasIdentity } from "work/proposals/negotiation/proposal-operation-identity";
import { readProposalOperationValue } from "work/proposals/negotiation/proposal-operation-projector";

/**
 * Adapters from every "a change happened" shape the app carries onto the one descriptor the UI renders.
 *
 * There are three such shapes, and they are not going away: the server's timeline entries (history), the
 * envelope's change lines (what is publishable to the other party), and the client's queued operations
 * (what a Save is about to persist). Each adapter's only job is to normalise; all wording comes from
 * `proposal-change-description`, so the Save dialog, the Submit dialog and the change history cannot
 * describe the same change in different words.
 */

/** A timeline entry — the per-field history and the Changes pane. */
export function describeProposalTimelineChange(
  entry: Pick<
    ProposalTimelineEntry,
    "kind" | "path" | "itemId" | "conflictKey" | "expectedValue" | "expectedValueExists" | "value" | "valueExists" | "sequence" | "sourceParty" | "createdDate" | "actorUserId"
  >,
  context: ProposalChangeDescriptionContext = {},
): ProposalChangeDescriptor {
  const path = entry.path ?? "";
  const previousText = entry.expectedValueExists
    ? proposalChangeValueText(path, entry.expectedValue, context)
    : undefined;
  const nextText = entry.valueExists ? proposalChangeValueText(path, entry.value, context) : undefined;

  return {
    key: proposalChangeKey(path, entry.itemId),
    path,
    fieldLabel: proposalChangeFieldLabel(path, entry.conflictKey),
    // The value fallback the other two adapters already had. Without it this was the one shape that
    // could not name its own item: history is the surface most likely to describe an item that no
    // longer exists in the projection the context resolves against, so a removed fee-schedule category
    // went unnamed precisely where naming it matters most. An entry carries the item's value itself —
    // the previous value when the change removed it — so it never needed the lookup to begin with.
    itemLabel:
      itemLabelFor(path, entry.itemId, context) ??
      proposalCollectionItemLabel(entry.valueExists ? entry.value : entry.expectedValue),
    action: proposalChangeActionFor({
      kind: entry.kind,
      previousExists: entry.expectedValueExists,
      nextExists: entry.valueExists,
    }),
    previousText,
    nextText,
    // The whole item the change landed on, so the fee schedule can be drawn as the category it is
    // rather than as the one column that moved — see ProposalChangeDescriptor.itemValue.
    itemValue: itemValueFor(path, entry.itemId, context),
    party: entry.sourceParty ?? undefined,
    actorName: entry.actorUserId ? context.resolveUserName?.(entry.actorUserId) : undefined,
    sequence: entry.sequence,
    createdDate: entry.createdDate,
  };
}

/**
 * A publishable change line, valued from the envelope's own projection.
 *
 * A change line records only "this field last changed"; the *new* value lives in the projection, so the
 * descriptor reads it back out at `path` (and at the collection item, when there is one).
 *
 * The *previous* value cannot come from there — the projection holds one value per path, the new one —
 * so it rides on the line itself, and only on the viewer's own unpublished lines (see
 * `ProposalChangeLine.PreviousValue`). Until it did, this was the one adapter of the three that could
 * not produce a `previousText`, which made the submit dialog the last surface describing a change in
 * different words from the save confirmation and the field history: those two drew the transition while
 * it showed the new value alone. A line without one still describes itself by its new value, so an
 * older API deploy reads exactly as it did before.
 */
export function describeProposalChangeLine(
  envelope: ProposalCollaborationEnvelope,
  line: ProposalChangeLine,
  context: ProposalChangeDescriptionContext = {},
): ProposalChangeDescriptor {
  const value = readProposalOperationValue(envelope.projection, line.path);
  const itemValue = line.itemId ? collectionItemValue(value, line.itemId) : undefined;
  // A line about ONE COLUMN of a fee-schedule category is valued from that column, not from the
  // category it belongs to. The projection holds the whole category, and describing a `feeRate` line by
  // it ran into `objectValueText`'s "named by its `name`" rule: the submit preview announced the changed
  // Rate as "Trademark Filing" — the row's own heading, offered as the new price. The column also
  // decides the action, so a cleared rate reads as cleared rather than as an update to a category that
  // still exists.
  const feeScheduleColumn = proposalFeeScheduleColumnFromConflictKey(
    isProposalFeeScheduleCategoryPath(line.path) ? line.conflictKey : undefined,
  );
  // The assigned member is a column of its own kind: a person, read through the same leaf shape the
  // stream carries it in (`{ teamMember: … }`) so it names who it is now — or, for a viewer the server
  // masks it from, says only that it is restricted (#4949).
  const feeScheduleTeamMember = isProposalFeeScheduleTeamMemberProperty(feeScheduleColumn);
  const feeScheduleAspect = feeScheduleColumn && !feeScheduleTeamMember ? feeScheduleColumn : undefined;
  const categoryValues = feeScheduleAspect ? readProposalFeeScheduleCategoryValues(itemValue) : undefined;
  const describedValue = feeScheduleAspect
    ? readProposalFeeScheduleAspectValue(itemValue, feeScheduleAspect)
    : feeScheduleTeamMember
      ? { [proposalFeeScheduleTeamMemberProperty]: readProposalFeeScheduleAspectValue(itemValue, proposalFeeScheduleTeamMemberProperty) ?? null }
      : line.itemId
        ? itemValue
        : value;
  const nextText =
    feeScheduleAspect && categoryValues
      ? proposalFeeScheduleAspectText(feeScheduleAspect, describedValue, categoryValues)
      : feeScheduleTeamMember && feeScheduleTeamMemberIsMasked(itemValue)
        ? proposalFeeScheduleRestrictedTeamMemberText
        : proposalChangeValueText(line.path, describedValue, context);
  const previousValue = line.previousValueExists ? line.previousValue : undefined;

  return {
    key: proposalChangeKey(line.conflictKey, line.itemId),
    path: line.path,
    fieldLabel: proposalChangeFieldLabel(line.path, line.conflictKey),
    itemLabel: itemLabelFor(line.path, line.itemId, context) ?? proposalCollectionItemLabel(itemValue),
    // Deliberately unchanged by the arrival of a previous value: a line says "Updated" or "Cleared" on
    // the strength of what it is sending, and routing it through `proposalChangeActionFor` would have
    // turned every line the server sends no previous value for — a counterparty's, an older deploy's —
    // into "Added". Whether a first-time set should say so is a separate question from this one.
    action: describedValue === undefined || describedValue === null ? "cleared" : "updated",
    previousText: line.previousValueExists
      ? proposalChangeValueText(line.path, previousValue, context)
      : undefined,
    nextText,
    // The projection already holds the whole item this line is about; reading it back costs nothing
    // and is what lets the submission summary draw the entire category.
    itemValue: itemValue ?? itemValueFor(line.path, line.itemId, context),
    party: line.lastChangedByParty,
    sequence: line.lastChangedSequence,
  };
}

/**
 * Paths that carry projection plumbing rather than a term anyone negotiates. Applying a fee-schedule
 * or team template records which template it came from; that bookkeeping belongs in the audit stream,
 * not in a list of "changes being saved" a human is asked to check — where it surfaced as a bare GUID
 * ("Client Fee Schedule Template Ids Added: d915b62a-…") beside the real change it accompanied.
 *
 * The backend draws the same line: `ProposalResolutionTargetPolicy` keeps template IDs out of the
 * negotiable terms for exactly this reason, "without creating invisible review work".
 */
const projectionMetadataPaths: ReadonlySet<string> = new Set([
  "/clientTeamTemplateIds",
  "/vendorTeamTemplateIds",
  "/clientFeeScheduleTemplateIds",
  "/vendorFeeScheduleTemplateIds",
  // Choosing a team writes its identity and its per-viewer masking flag alongside the roster. Both
  // label as plain "Team", so picking one team produced a stack of interchangeable "Team Added" rows —
  // one reading "No" (the masking flag) and one a bare marketplace GUID — around the single row that
  // meant anything. The team's identity is chosen as a whole and masking is recomputed per viewer on
  // every envelope build (see ProposalCollaborationPathPolicy.FieldPaths); neither is a term.
  "/clientTeam/isMasked",
  "/vendorTeam/isMasked",
  "/clientTeam/marketplaceId",
  "/vendorTeam/marketplaceId",
]);

export function isProjectionMetadataPath(path: string | undefined): boolean {
  return path !== undefined && projectionMetadataPaths.has(path);
}

/** A queued content operation — what a Save is about to persist, before any round trip. */
export function describeProposalOperation(
  operation: ProposalContentOperation,
  context: ProposalChangeDescriptionContext = {},
): ProposalChangeDescriptor | undefined {
  if (operation.kind === "ResolutionDecision") return undefined;
  if (isProjectionMetadataPath(operation.path)) return undefined;

  const itemId = "itemId" in operation ? operation.itemId : undefined;
  const previousValue = "expectedValue" in operation ? operation.expectedValue : undefined;
  const nextValue = "value" in operation ? operation.value : undefined;

  // For a proposal scalar, `null` IS absence — the projection serializes "not set" as null while the
  // editor round-trips it as undefined (see `coalesceAbsentScalar`). Testing only against `undefined`
  // counted a null as a real value, so an operation touching a never-set Response Due By described
  // itself as that field being newly given a value, showing the user a change to a field they had not
  // touched and could not see a value for.
  const previousExists = valueIsPresent(previousValue);
  const nextExists = valueIsPresent(nextValue);
  // Neither side holds a value, so there is nothing to tell the user about — announcing it is worse
  // than silence, because it names a field that did not change.
  if (!previousExists && !nextExists && operation.kind === "FieldSet") return undefined;

  return {
    key: proposalChangeKey(operation.conflictKey, itemId),
    path: operation.path,
    fieldLabel: proposalChangeFieldLabel(operation.path, operation.conflictKey),
    // The same value fallback the timeline adapter states, and for the same reason — except that a
    // queued REMOVAL is the one operation whose item cannot be looked up in the context at all: the
    // context resolves against the effective projection (projection plus pending operations), and this
    // operation is exactly what took the item out of it. Reading only `nextValue`, which a removal
    // does not have, left the Save confirmation asking a user to confirm removing "a" policy document.
    itemLabel:
      itemLabelFor(operation.path, itemId, context) ??
      proposalCollectionItemLabel(nextExists ? nextValue : previousValue),
    action: proposalChangeActionFor({
      kind: operation.kind,
      previousExists,
      nextExists,
    }),
    previousText: proposalChangeValueText(operation.path, previousValue, context),
    nextText: proposalChangeValueText(operation.path, nextValue, context),
    // A queued leaf update carries only its own column, so the category it belongs to comes from the
    // effective projection — the same place `itemLabel` above comes from, at whole-item granularity.
    itemValue: itemValueFor(operation.path, itemId, context) ?? (nextExists ? nextValue : previousValue),
  };
}

/**
 * A queued reviewer change.
 *
 * Reviewers are not proposal content — they never appear on the negotiation's content paths — but a Save
 * that grants or revokes edit rights is exactly the kind of change a leader wants spelled out
 * ("Reviewers Updated: Jane Doe → Full edit"), so they describe through the same vocabulary.
 */
/**
 * Reviewers are not proposal content, so they have no negotiation path of their own; this is the one
 * they describe under, and it is what tells a reviewer descriptor apart from a content one afterwards.
 */
export const reviewerDescriptorPath = "/reviewers";

export function describeProposalReviewerChange(
  change: ProposalReviewerChange,
  context: ProposalChangeDescriptionContext = {},
): ProposalChangeDescriptor {
  const reviewerName = context.resolveUserName?.(change.reviewerUserId) ?? "Reviewer";
  const descriptor: ProposalChangeDescriptor = {
    key: proposalChangeKey("reviewers", change.reviewerUserId),
    path: reviewerDescriptorPath,
    // Singular: one row is about one person, not about the roster. "Added Reviewer: Jane Doe (Full edit)"
    // is the same shape as the history's "Added Team Member: Jane Doe".
    fieldLabel: "Reviewer",
    itemLabel: reviewerName,
    action: "updated",
  };

  switch (change.kind) {
    case "Assign":
      return {
        ...descriptor,
        action: "added",
        nextText: proposalReviewerAccessLabel(change.canEdit),
      };
    case "Remove":
      return { ...descriptor, action: "removed" };
    case "SetCanEdit":
      return {
        ...descriptor,
        previousText: proposalReviewerAccessLabel(change.expectedCanEdit),
        nextText: proposalReviewerAccessLabel(change.canEdit),
      };
    case "SetCustomMessage":
      return { ...descriptor, nextText: change.customMessage ? "Message updated" : "Message cleared" };
  }
}

/** Whether the projection withholds this category's member from the viewer (`ProposalFeeScheduleTeamMemberMasking`). */
function feeScheduleTeamMemberIsMasked(itemValue: unknown): boolean {
  return typeof itemValue === "object" && itemValue !== null && (itemValue as { teamMemberIsMasked?: unknown }).teamMemberIsMasked === true;
}

/** A proposal value is present only when it is neither absent nor an explicit null — see above. */
function valueIsPresent(value: unknown): boolean {
  return value !== undefined && value !== null;
}

function itemLabelFor(
  path: string,
  itemId: string | null | undefined,
  context: ProposalChangeDescriptionContext,
): string | undefined {
  if (!itemId) return undefined;
  return context.resolveItemLabel?.(path, itemId);
}

function itemValueFor(
  path: string,
  itemId: string | null | undefined,
  context: ProposalChangeDescriptionContext,
): unknown {
  if (!itemId) return undefined;
  return context.resolveItemValue?.(path, itemId);
}

/**
 * The item a change landed on, found by the SAME identity rule the rest of the negotiation uses.
 *
 * Comparing a handful of id keys by raw string equality could not find a team member: a member's identity
 * is the composite `userId:legalEntityId` the operation layer mints (a person can sit on a proposal for
 * more than one entity), it is case-insensitive, and a document's identity may live on its nested info.
 * So a member change found no value, described itself as "cleared", and rendered as a bare "Team Member"
 * row naming nobody — in the one dialog whose job is to say who is being sent to the counterparty.
 */
function collectionItemValue(collection: unknown, itemId: string): unknown {
  if (!Array.isArray(collection)) return undefined;
  return collection.find((item) => proposalCollectionItemHasIdentity(item, itemId));
}

/**
 * The display name carried by a collection item's own value — a fee-schedule category's `name`, a
 * document's `fileName`. Used when the change description's context cannot resolve the item from the
 * current projection, which is always the case for an item that has since been removed, and returns
 * undefined for a scalar (a string, a number, or an object with no name-shaped key).
 *
 * A document keeps its name one level down, in `documentInfo` — the display metadata the local
 * projection carries and the wire deliberately does not (`canonicalProposalCollectionItem`). So this
 * fallback, written for a removed fee category, silently did nothing for the case it matters most for:
 * removing a policy document asked the user to confirm "Removed Client Policy Document" with no name
 * on a proposal holding several. Nested lookup is one level deep and by known key, so a scalar object
 * that happens to hold an unrelated `documentInfo` still cannot produce a label.
 */
export function proposalCollectionItemLabel(value: unknown): string | undefined {
  const direct = collectionItemLabelFromOwnKeys(value);
  if (direct) return direct;
  if (!value || typeof value !== "object") return undefined;
  return collectionItemLabelFromOwnKeys((value as Record<string, unknown>).documentInfo);
}

function collectionItemLabelFromOwnKeys(value: unknown): string | undefined {
  if (!value || typeof value !== "object") return undefined;
  const candidate = value as Record<string, unknown>;
  const named = nameOf(candidate);
  if (named) return named;
  // A proposal document is a reference wrapping the file's own record, so its name is one level down.
  const documentInfo = candidate.documentInfo;
  return documentInfo && typeof documentInfo === "object"
    ? nameOf(documentInfo as Record<string, unknown>)
    : undefined;
}

function nameOf(candidate: Record<string, unknown>): string | undefined {
  for (const key of ["name", "title", "displayName", "fileName", "categoryName"]) {
    const label = candidate[key];
    if (typeof label === "string" && label) return label;
  }
  return undefined;
}
